import React, { useState } from "react";
import { IdCard, Eye } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import api, { formatApiError } from "@/lib/api";
import { PassportPreviewDialog } from "./PassportPreviewDialog";

const STATUS_STYLES = {
    new: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
    contacted: "bg-blue-500/15 text-blue-600 dark:text-blue-400",
    shared: "bg-violet-500/15 text-violet-600 dark:text-violet-400",
    closed: "bg-muted text-muted-foreground",
};

export const PassportRequestsTab = ({ rows, onChanged }) => {
    const [previewId, setPreviewId] = useState(null);

    const setStatus = async (req, status) => {
        try {
            await api.patch(`/admin/passport-requests/${req.id}`, { status });
            toast.success(`Request marked as ${status}.`);
            onChanged();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    if (!rows || rows.length === 0) {
        return (
            <div className="card-soft p-8 text-center">
                <IdCard size={22} className="text-brand-green mx-auto" />
                <h3 className="mt-3 font-heading font-semibold text-brand-navy dark:text-slate-100">No Talent Passport requests yet.</h3>
                <p className="text-brand-muted text-sm mt-1">When an employer requests a candidate&apos;s full Talent Passport from their AI Hiring Brief™, it will appear here.</p>
            </div>
        );
    }

    return (
        <>
            <div className="space-y-3">
                {rows.map((req) => (
                    <div key={req.id} data-testid={`passport-request-${req.id}`} className="card-soft p-5 flex flex-col md:flex-row md:items-center gap-4">
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <h3 className="font-heading font-semibold text-brand-navy dark:text-slate-100">{req.candidate_name || "Candidate"}</h3>
                                {req.match_score != null && <Badge variant="outline" className="text-xs mono-score">{req.match_score}% match</Badge>}
                                <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide ${STATUS_STYLES[req.status] || STATUS_STYLES.new}`}>{req.status}</span>
                            </div>
                            <div className="text-sm text-brand-muted mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                                <span>Role: {req.job_title || "—"}</span>
                                <span>· {req.company_name || req.employer_name}</span>
                                <span>· {req.employer_email}</span>
                            </div>
                            <div className="text-xs text-brand-muted mt-0.5">{req.created_at ? new Date(req.created_at).toLocaleString() : ""}</div>
                        </div>
                        <div className="shrink-0 flex items-center gap-2 w-full md:w-auto">
                            <button
                                onClick={() => setPreviewId(req.id)}
                                data-testid={`request-preview-${req.id}`}
                                className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3.5 py-2 text-xs font-semibold text-brand-navy dark:text-slate-100 hover:bg-muted transition-colors"
                            >
                                <Eye size={13} /> Preview passport
                            </button>
                            <div className="w-full md:w-44">
                                <Select value={req.status} onValueChange={(v) => setStatus(req, v)}>
                                    <SelectTrigger data-testid={`request-status-${req.id}`} className="text-sm">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="new">New</SelectItem>
                                        <SelectItem value="contacted">Contacted</SelectItem>
                                        <SelectItem value="shared">Passport shared</SelectItem>
                                        <SelectItem value="closed">Closed</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            <PassportPreviewDialog open={!!previewId} onOpenChange={(v) => !v && setPreviewId(null)} requestId={previewId} />
        </>
    );
};

export default PassportRequestsTab;
